import { useEffect, useId, useState } from 'react';

import styles from './RoutingDiagram.module.css';

export function RoutingDiagram() {
  const titleId = useId();
  const descId = useId();
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }

    return window.matchMedia('(max-width: 767px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return (
    <div className={styles.wrapper}>
      {isMobile ? (
        <svg
          aria-describedby={descId}
          aria-labelledby={titleId}
          className={styles.diagram}
          role="img"
          viewBox="0 0 340 640"
        >
          <title id={titleId}>How Kudu routes AI requests</title>
          <desc id={descId}>
            A three-step vertical flow showing a student or faculty query
            reaching Kudu for anonymization and course context before being
            routed to a swappable frontier model.
          </desc>

          <defs>
            <marker
              id="routing-arrow-head-mobile"
              markerHeight="8"
              markerWidth="8"
              orient="auto"
              refX="7"
              refY="4"
            >
              <path className={styles.arrowHead} d="M0,0 L8,4 L0,8" />
            </marker>
          </defs>

          <rect className={styles.box} height="132" rx="12" width="300" x="20" y="20" />
          <rect
            className={`${styles.box} ${styles.boxEmphasis}`}
            data-box="kudu"
            height="132"
            rx="12"
            width="300"
            x="20"
            y="254"
          />
          <rect className={styles.box} height="132" rx="12" width="300" x="20" y="488" />

          <path
            className={styles.arrow}
            d="M 170 152 L 170 226"
            markerEnd="url(#routing-arrow-head-mobile)"
          />
          <path
            className={styles.arrow}
            d="M 170 386 L 170 460"
            markerEnd="url(#routing-arrow-head-mobile)"
          />

          <text className={styles.title} textAnchor="middle" x="170" y="72">
            Student /
          </text>
          <text className={styles.title} textAnchor="middle" x="170" y="104">
            Faculty query
          </text>

          <text className={styles.title} textAnchor="middle" x="170" y="306">
            Kudu
          </text>
          <text className={styles.label} textAnchor="middle" x="170" y="338">
            anonymize +
          </text>
          <text className={styles.label} textAnchor="middle" x="170" y="366">
            add context
          </text>

          <text className={styles.title} textAnchor="middle" x="170" y="540">
            Frontier model
          </text>
          <text className={styles.label} textAnchor="middle" x="170" y="572">
            swappable
          </text>
        </svg>
      ) : (
        <svg
          aria-describedby={descId}
          aria-labelledby={titleId}
          className={styles.diagram}
          role="img"
          viewBox="0 0 980 220"
        >
          <title id={titleId}>How Kudu routes AI requests</title>
          <desc id={descId}>
            A three-step flow showing a student or faculty query reaching Kudu
            for anonymization and course context before being routed to a
            swappable frontier model.
          </desc>

          <defs>
            <marker
              id="routing-arrow-head-desktop"
              markerHeight="8"
              markerWidth="8"
              orient="auto"
              refX="7"
              refY="4"
            >
              <path className={styles.arrowHead} d="M0,0 L8,4 L0,8" />
            </marker>
          </defs>

          <rect className={styles.box} height="128" rx="12" width="250" x="0" y="46" />
          <rect
            className={`${styles.box} ${styles.boxEmphasis}`}
            data-box="kudu"
            height="128"
            rx="12"
            width="250"
            x="364"
            y="46"
          />
          <rect className={styles.box} height="128" rx="12" width="250" x="728" y="46" />

          <path
            className={styles.arrow}
            d="M 250 110 L 342 110"
            markerEnd="url(#routing-arrow-head-desktop)"
          />
          <path
            className={styles.arrow}
            d="M 614 110 L 706 110"
            markerEnd="url(#routing-arrow-head-desktop)"
          />

          <text className={styles.title} textAnchor="middle" x="125" y="92">
            Student /
          </text>
          <text className={styles.title} textAnchor="middle" x="125" y="124">
            Faculty query
          </text>

          <text className={styles.title} textAnchor="middle" x="489" y="88">
            Kudu
          </text>
          <text className={styles.label} textAnchor="middle" x="489" y="120">
            anonymize +
          </text>
          <text className={styles.label} textAnchor="middle" x="489" y="148">
            add context
          </text>

          <text className={styles.title} textAnchor="middle" x="853" y="92">
            Frontier
          </text>
          <text className={styles.title} textAnchor="middle" x="853" y="124">
            model
          </text>
          <text className={styles.label} textAnchor="middle" x="853" y="152">
            swappable
          </text>
        </svg>
      )}
    </div>
  );
}
